// @ts-nocheck
import gameService from "@/integration/game/service/game.service.ts";
import { queryOptions } from "@tanstack/react-query";
import { ISearchPagination } from "@/integration/application/interfaces/searchPagination.interface.ts";
import teamService from "@/integration/team/services/team.service.ts";
import badgetService from "@/integration/badget/services/badget.service.ts";
import teamActivityService from "@/integration/team/services/team-activity.service.ts";
import teamAchievementsService from "@/integration/team/services/team-achievements.service.ts";
import teamCommunicationsService from "@/integration/team/services/team-communications.service.ts";
import teamRecruitmentService from "@/integration/team/services/team-recruitment.service.ts";
import teamSponsorsService from "@/integration/team/services/team-sponsors.service.ts";
import { TeamActivityType } from "@/integration/team/interfaces/team-activity.interface";
import { MessageType } from "@/integration/team/interfaces/team-communications.interface";
import { QUERY_KEYS } from "@integration/factory/queryKeys.ts";

const optionsQuery = () => {
  const { getGameById, getAllGames, getAllGamesPublic } = gameService();
  const {
    getProfileTeam,
    getInviteNotificationsUser,
    getTeamMembers,
    getTeamByIdPublic,
    getTeamMembersByIdPublic,
    searchTeamByNameAndTag,
    getStatsTeamByIdPublic,
    searchMyTeamsByNameAndTag,
  } = teamService();

  const { getBadgetsTeamByIdPublic } = badgetService();

  const { getTeamActivities, getTeamActivityById } = teamActivityService();

  const {
    getTeamAchievements,
    getTeamAchievementById,
    createTeamAchievement,
    updateTeamAchievement,
    deleteTeamAchievement,
  } = teamAchievementsService();

  const {
    getTeamCommunications,
    getTeamCommunicationById,
    createTeamCommunication,
    updateTeamCommunication,
    deleteTeamCommunication,
    markCommunicationAsRead,
    getUnreadCommunicationsCount,
  } = teamCommunicationsService();

  const {
    getTeamRecruitment,
    createTeamRecruitment,
    updateTeamRecruitment,
    toggleRecruitmentStatus,
  } = teamRecruitmentService();

  const {
    getTeamSponsors,
    getTeamSponsorById,
    createTeamSponsor,
    updateTeamSponsor,
    deleteTeamSponsor,
  } = teamSponsorsService();

  const optionsFetchGameId = (id: string) => {
    return queryOptions({
      queryKey: ["gameById", id],
      queryFn: () => getGameById(id),
      enabled: !!id,
    });
  };

  const optionsFetchGames = () => {
    return queryOptions({ queryKey: ["allGames"], queryFn: getAllGames });
  };

  const optionsFetchGamesPublic = () => {
    return queryOptions({
      queryKey: ["allGamesPublic"],
      queryFn: getAllGamesPublic,
      staleTime: 10 * 60 * 1000, // 10 minutes
      cacheTime: 15 * 60 * 1000, // 15 minutes
      refetchOnWindowFocus: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
    });
  };

  const optionsFetchTeamMembers = (teamId: string) => {
    return queryOptions({
      queryKey: ["teamMembers", teamId],
      queryFn: () => getTeamMembers(teamId),
      enabled: !!teamId,
    });
  };

  const optionsFetchSearchTeamByNameAndTag = (
    searchPagination: ISearchPagination
  ) =>
    queryOptions({
      queryKey: ["searchTeamByNameAndTag", searchPagination],
      queryFn: async () => {
        const response = await searchTeamByNameAndTag(searchPagination);

        if (response) {
          return {
            data: response.data,
            page: response.page,
            limit: response.limit,
            total: response.total,
            totalPage: response.totalPage,
          };
        }
        return { data: [], page: 0, limit: 0 };
      },
    });

  const optionsFetchSearchMyTeams = (searchPagination: ISearchPagination) =>
    queryOptions({
      queryKey: ["searchMyTeams", searchPagination],
      queryFn: async () => {
        const response = await searchMyTeamsByNameAndTag(searchPagination);

        if (response) {
          return {
            data: response.data,
            page: response.page,
            limit: response.limit,
            total: response.total,
            totalPage: response.totalPage,
          };
        }
        return { data: [], page: 0, limit: 0 };
      },
    });

  const optionsFetchTeamById = (teamId: string) => {
    return queryOptions({
      queryKey: [QUERY_KEYS.TEAM.root, teamId],
      queryFn: () => getProfileTeam(teamId),
      enabled: !!teamId,
      staleTime: 1000 * 60 * 5, // Dados considerados frescos por 5 minutos
      cacheTime: 1000 * 60 * 10, // Cache permanece por 10 minutos após não ser usado
    });
  };

  const optionsFetchTeamByIdPublic = (teamId: string) => {
    return queryOptions({
      queryKey: ["teamByIdPublic", teamId],
      queryFn: () => getTeamByIdPublic(teamId),
      staleTime: 1000 * 60 * 5, // Dados considerados frescos por 5 minutos
      cacheTime: 1000 * 60 * 10, // Cache permanece por 10 minutos após não ser usado
    });
  };

  const optionsFetchTeamMembersByIdPublic = (teamId: string) => {
    return queryOptions({
      queryKey: ["teamMembersByIdPublic", teamId],
      queryFn: () => getTeamMembersByIdPublic(teamId!),
      enabled: !!teamId,
    });
  };

  const optionsFetchTeamNotifications = (isAuthenticated: boolean) => {
    return queryOptions({
      queryKey: ["teamNotificarionUser"],
      queryFn: getInviteNotificationsUser,
      enabled: isAuthenticated,
    });
  };

  // Se você quiser controlar o "enabled" externamente
  const optionsFetchTeamPerformanceStatsPublic = (teamId: string) => {
    return queryOptions({
      queryKey: ["teamByIdStats", teamId],
      queryFn: () => getStatsTeamByIdPublic(teamId),
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10,
    });
  };

  const optionsFetchBadgetTeamPublic = (entityId: string) => {
    return queryOptions({
      queryKey: ["teamByIdBadget", entityId],
      queryFn: () => getBadgetsTeamByIdPublic(entityId!),
      enabled: !!entityId,
    });
  };

  const optionsFetchTeamActivities = (
    teamId: string,
    page: number = 1,
    limit: number = 10,
    actionType?: TeamActivityType,
    timeFilter?: number
  ) => {
    return queryOptions({
      queryKey: ["teamActivities", teamId, page, limit, actionType, timeFilter],
      queryFn: () =>
        getTeamActivities(teamId, page, limit, actionType, timeFilter),
      enabled: !!teamId,
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
    });
  };

  const optionsFetchTeamActivityById = (teamId: string, activityId: string) => {
    return queryOptions({
      queryKey: ["teamActivity", teamId, activityId],
      queryFn: () => getTeamActivityById(teamId, activityId),
      enabled: !!teamId && !!activityId,
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
    });
  };

  // Team Achievements Query Options

  const optionsFetchTeamAchievements = (
    teamId: string,
    page: number = 1,
    limit: number = 10
  ) => {
    return queryOptions({
      queryKey: ["teamAchievements", teamId, page, limit],
      queryFn: () => getTeamAchievements(teamId, page, limit),
      enabled: !!teamId,
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
    });
  };

  const optionsFetchTeamAchievementById = (
    teamId: string,
    achievementId: string
  ) => {
    return queryOptions({
      queryKey: ["teamAchievement", teamId, achievementId],
      queryFn: () => getTeamAchievementById(teamId, achievementId),
      enabled: !!teamId && !!achievementId,
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
    });
  };

  // Team Communications Query Options

  const optionsFetchTeamCommunications = (
    teamId: string,
    page: number = 1,
    limit: number = 10,
    messageType?: MessageType,
    isGlobal?: boolean
  ) => {
    return queryOptions({
      queryKey: [
        "teamCommunications",
        teamId,
        page,
        limit,
        messageType,
        isGlobal,
      ],
      queryFn: () =>
        getTeamCommunications(teamId, page, limit, messageType, isGlobal),
      enabled: !!teamId,
      staleTime: 1000 * 60 * 2, // 2 minutes (shorter for communications)
      cacheTime: 1000 * 60 * 10, // 10 minutes
    });
  };

  const optionsFetchTeamCommunicationById = (
    teamId: string,
    communicationId: string
  ) => {
    return queryOptions({
      queryKey: ["teamCommunication", teamId, communicationId],
      queryFn: () => getTeamCommunicationById(teamId, communicationId),
      enabled: !!teamId && !!communicationId,
      staleTime: 1000 * 60 * 2, // 2 minutes
      cacheTime: 1000 * 60 * 10, // 10 minutes
    });
  };

  const optionsFetchUnreadCommunicationsCount = (teamId: string) => {
    return queryOptions({
      queryKey: ["unreadCommunicationsCount", teamId],
      queryFn: () => getUnreadCommunicationsCount(teamId),
      enabled: !!teamId,
      staleTime: 1000 * 30, // 30 seconds (very short for unread count)
      cacheTime: 1000 * 60 * 5, // 5 minutes
      refetchInterval: 1000 * 60, // Refetch every minute
    });
  };

  // Team Recruitment Query Options

  const optionsFetchTeamRecruitment = (teamId: string) => {
    return queryOptions({
      queryKey: ["teamRecruitment", teamId],
      queryFn: () => getTeamRecruitment(teamId),
      enabled: !!teamId,
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
    });
  };

  // Team Sponsors Query Options

  const optionsFetchTeamSponsors = (
    teamId: string,
    page: number = 1,
    limit: number = 10
  ) => {
    return queryOptions({
      queryKey: ["teamSponsors", teamId, page, limit],
      queryFn: () => getTeamSponsors(teamId, page, limit),
      enabled: !!teamId,
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
    });
  };

  const optionsFetchTeamSponsorById = (
    teamId: string,
    sponsorId: string
  ) => {
    return queryOptions({
      queryKey: ["teamSponsor", teamId, sponsorId],
      queryFn: () => getTeamSponsorById(teamId, sponsorId),
      enabled: !!teamId && !!sponsorId,
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
    });
  };

  return {
    optionsFetchGameId,
    optionsFetchGames,
    optionsFetchTeamMembers,
    optionsFetchTeamById,
    optionsFetchTeamNotifications,
    optionsFetchTeamByIdPublic,
    optionsFetchTeamMembersByIdPublic,
    optionsFetchSearchTeamByNameAndTag,
    optionsFetchGamesPublic,
    optionsFetchTeamPerformanceStatsPublic,
    optionsFetchBadgetTeamPublic,
    optionsFetchSearchMyTeams,
    optionsFetchTeamActivities,
    optionsFetchTeamActivityById,
    optionsFetchTeamAchievements,
    optionsFetchTeamAchievementById,
    optionsFetchTeamCommunications,
    optionsFetchTeamCommunicationById,
    optionsFetchUnreadCommunicationsCount,
    optionsFetchTeamRecruitment,
    optionsFetchTeamSponsors,
    optionsFetchTeamSponsorById,
  };
};

export const {
  optionsFetchTeamByIdPublic,
  optionsFetchGameId,
  optionsFetchGames,
  optionsFetchTeamMembers,
  optionsFetchTeamById,
  optionsFetchTeamNotifications,
  optionsFetchTeamMembersByIdPublic,
  optionsFetchSearchTeamByNameAndTag,
  optionsFetchGamesPublic,
  optionsFetchTeamPerformanceStatsPublic,
  optionsFetchBadgetTeamPublic,
  optionsFetchSearchMyTeams,
  optionsFetchTeamActivities,
  optionsFetchTeamActivityById,
  optionsFetchTeamAchievements,
  optionsFetchTeamAchievementById,
  optionsFetchTeamCommunications,
  optionsFetchTeamCommunicationById,
  optionsFetchUnreadCommunicationsCount,
  optionsFetchTeamRecruitment,
  optionsFetchTeamSponsors,
  optionsFetchTeamSponsorById
} = optionsQuery();
